import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import errorHandler from "../../../utils/errorHandler";
import { toast } from "react-toastify";
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";
import BTNLoader from "../../../lib/components/BTNLoader";
import * as Yup from "yup";
import userservice from "../../../service/userservice";

const schema = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  phone: Yup.string().required(),
  email: Yup.string().trim().email("Email must be valid").required("Email address is required"),
});

const defaultValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};
const EditUser = ({ isOpen, onClose, refetch, userData }) => {
  const formHook = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: defaultValues,
  });

  const errors = formHook.formState.errors;
  const control = formHook.control;
  const handleSubmit = formHook.handleSubmit;
  const reset = formHook.reset;

  const mutation = useMutation({
    mutationFn: (data) => userservice.updateUser(data, { id: userData?.id }),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("User updated successfully.");
      refetch();
      onClose();
    },
  });

  async function save(data) {
    mutation.mutate({
      firstName: data?.firstName,
      lastName: data?.lastName,
      phone: data?.phone,
    });
  }
  const { isPending } = mutation;

  React.useEffect(() => {
    reset({
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      email: userData?.email,
      phone: userData?.phone,
    });
  }, [isOpen]);

  return (
    <React.Fragment>
      <Dialog
        open={isOpen}
        maxWidth="sm"
        PaperProps={{
          className: "w-100",
          component: "form",
          onSubmit: handleSubmit(save),
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Edit User</div>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
        <DialogContent className="h-100 overflow-auto">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label className="d-block">
                  First Name
                  <span className="text-danger">*</span>
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Enter First Name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["firstName"]?.message}
                      error={!!errors["firstName"]}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label className="d-block">
                  Last Name
                  <span className="text-danger">*</span>
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Enter Last Name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["lastName"]?.message}
                      error={!!errors["lastName"]}
                    />
                  )}
                />
              </div>
            </Grid>
          </Grid>

          <div className="mb-4">
            <label className="d-block">
              Email
              <span className="text-danger">*</span>
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  disabled
                  type="email"
                  size="small"
                  variant="outlined"
                  placeholder="Enter email address"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["email"]?.message}
                  error={!!errors["email"]}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <label className="d-block">Phone Number</label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type="text"
                  size="small"
                  variant="outlined"
                  placeholder="Enter phone number"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["phone"]?.message}
                  error={!!errors["phone"]}
                />
              )}
            />
          </div>
          <div className="mb-4 d-none">
            <label className="d-block">
              Organization Name
              <span className="text-danger">*</span>
            </label>
            <Controller
              name="organizationName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type="text"
                  size="small"
                  variant="outlined"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["organizationName"]?.message}
                  error={!!errors["organizationName"]}
                />
              )}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            startIcon={<BTNLoader isLoading={isPending} />}
            variant="contained"
            color={"secondary"}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default EditUser;
